import { Fragment, useEffect, useRef, useState } from "react";
import { runDurationLabel, type DocumentationItem, type LearnerHistoryPage, type LearnerRunHistory, type LearnerRunSummary } from "@calega/exercise-engine";
import { api } from "../lib/api";
import { runStatusLabels } from "./qualitative-analysis/presentation";

const timestamp = (value: string | null) => value ? new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Nicht gespeichert";

export function LearnerHistoryItem({ item, flashcards, label, attemptLabel }: { item: DocumentationItem; flashcards: boolean; label: string; attemptLabel?: string }) {
  return <article className={`${flashcards ? "unrated" : item.status === "uncertain" ? "uncertain" : item.correct === true ? "correct" : item.correct === false ? "wrong" : "unrated"}${item.wall ? " has-wall" : ""}`}>
    <div className="run-item-status"><span>{label}</span><strong>{flashcards ? "Selbsteinschätzung" : item.status === "uncertain" ? "Unsicher" : item.correct === true ? "Richtig" : item.correct === false ? "Falsch" : "Ohne Bewertung"}</strong></div>
    {item.wall ? <div className="run-wall-documentation">
      <span className="run-wall-rule">Jeder obere Stein ist die Summe der beiden Steine darunter.</span>
      <div className="run-wall-attempts">{item.wall.attempts.map((attempt, index) => <figure className={`run-wall-attempt ${attempt.correct ? "correct" : "wrong"}`} key={index}>
        <figcaption>{attempt.label} · {attempt.correct ? "Richtig" : "Fehler markiert"}</figcaption>
        <div className="run-wall" aria-label={attempt.label}>{attempt.rows.map((row, rowIndex) => <div className="run-wall-row" key={rowIndex}>{row.map((cell) => <div className={`run-wall-cell ${cell.tone}`} key={cell.key} aria-label={cell.tone === "fixed" ? `Vorgegeben: ${cell.value}` : `Deine Antwort: ${cell.value ?? "fehlt"}, richtig: ${cell.expected}`}>
          <strong>{cell.value ?? "–"}</strong>{cell.tone === "wrong" && <small>richtig {cell.expected}</small>}
        </div>)}</div>)}</div>
      </figure>)}</div>
    </div> : <>
      {item.tokens ? <p className="run-item-tokens">{item.tokens.map((token, index) => <span className={token.tone} key={index}>{token.text}{token.after}</span>)}</p> : <p className="run-item-prompt">{flashcards && <strong>Vorderseite: </strong>}{item.prompt}</p>}
      <dl className="run-item-answer">
        {item.response && <div><dt>{flashcards ? "Deine Einschätzung" : "Deine Antwort"}</dt><dd>{item.response}</dd></div>}
        {item.expected && <div><dt>{flashcards ? "Rückseite" : "Richtige Lösung"}</dt><dd>{item.expected}</dd></div>}
      </dl>
      {item.notes.length > 0 && <ul className="run-item-notes">{item.notes.map((note, index) => <li key={index}>{note}</li>)}</ul>}
    </>}
    {attemptLabel && <small className="learner-attempt-heading">{attemptLabel}</small>}
  </article>;
}

function Result({ run }: { run: LearnerRunSummary }) {
  return <>{run.exerciseType === "flashcards" ? "Selbsteinschätzung" : run.status === "STARTED" ? "Noch offen" : run.score === null || run.maxScore === null ? "Ergebnis nicht gespeichert" : `${run.score} / ${run.maxScore} richtig`}</>;
}

export function LearnerHistoryDetail({ detail, inline = false }: { detail: LearnerRunHistory; inline?: boolean }) {
  const { run } = detail;
  return <>
    {inline ? <h3 className="learner-history-detail-title">Konkreter Aufgabenverlauf</h3> : <h2>{run.title}</h2>}
    <dl className="run-documentation-summary">
      <div><dt>Beginn</dt><dd>{timestamp(run.startedAt)}</dd></div>
      <div><dt>Ende</dt><dd>{run.status === "STARTED" ? "Noch offen" : timestamp(run.completedAt)}</dd></div>
      <div><dt>Dauer</dt><dd>{run.durationSeconds === null && run.status !== "STARTED" ? "Nicht gespeichert" : runDurationLabel(run)}</dd></div>
      <div><dt>Status</dt><dd>{runStatusLabels[run.status] ?? "Importiert"}</dd></div>
      <div><dt>Ergebnis</dt><dd><Result run={run} /></dd></div>
    </dl>
    {run.abortReason && <p>{run.abortReason === "INACTIVITY" ? "Dieser Durchgang wurde nach 15 Minuten ohne Eingabe beendet." : "Die Übungszeit endete ohne gespeicherte Antwort."}</p>}
    {run.dataOrigin === "legacy_v1" && <p className="muted">Aus V1 übernommen. Fehlende historische Angaben werden nicht ergänzt.</p>}
    {run.status === "STARTED" ? <p>Deine Antworten und Lösungen kannst du nach Beendigung des Durchgangs ansehen.</p> : <LearnerHistoryTasks detail={detail} />}
  </>;
}

export function LearnerHistoryTasks({ detail }: { detail: LearnerRunHistory }) {
  const { run, tasks } = detail;
  return <>
    {tasks.length === 0 && <p>Für diesen Durchgang sind keine Einzelantworten gespeichert.</p>}
    <div className="run-documentation-items">{tasks.flatMap((task, index) => task.attempts.flatMap((attempt, attemptIndex) => attempt.items.map((item, itemIndex) => <LearnerHistoryItem
      key={`${task.key}:${attemptIndex}:${itemIndex}`} item={item} flashcards={run.exerciseType === "flashcards"}
      label={`${run.exerciseType === "flashcards" ? "Karte" : "Aufgabe"} ${index + 1}`}
      attemptLabel={task.attempts.length > 1 ? `Versuch ${attemptIndex + 1}${attempt.savedAt ? ` · ${timestamp(attempt.savedAt)}` : ""}` : undefined}
    />)))}</div>
  </>;
}

function HistoryRunDetail({ runId, inline = false }: { runId: string; inline?: boolean }) {
  const [detail, setDetail] = useState<LearnerRunHistory | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setError("");
    api<LearnerRunHistory>(`/learning/history/${encodeURIComponent(runId)}`, { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setDetail(data); })
      .catch((caught) => { if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "Dein Verlauf konnte nicht geladen werden."); });
    return () => controller.abort();
  }, [runId, retry]);
  if (error) return <div className="alert error" role="alert">{error} <button className="secondary" onClick={() => setRetry((value) => value + 1)}>Erneut laden</button></div>;
  if (!detail) return <p role="status">Dein Verlauf wird geladen …</p>;
  return <LearnerHistoryDetail detail={detail} inline={inline} />;
}

export function LearnerHistory({ runId, embedded = false }: { runId?: string; embedded?: boolean }) {
  const [runs, setRuns] = useState<LearnerRunSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const request = useRef<AbortController | null>(null);
  const failedCursor = useRef<string | undefined>(undefined);

  async function load(cursor?: string) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    failedCursor.current = cursor;
    setLoading(true);
    setError("");
    try {
      const data = await api<LearnerHistoryPage>(`/learning/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, { signal: controller.signal });
      if (!controller.signal.aborted) { setRuns((previous) => cursor ? [...previous, ...data.runs.filter((run) => !previous.some(({ id }) => id === run.id))] : data.runs); setNextCursor(data.nextCursor); }
    } catch (caught) {
      if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "Dein Verlauf konnte nicht geladen werden.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  useEffect(() => {
    if (!runId) void load(failedCursor.current);
    return () => request.current?.abort();
  }, [runId, retry]);
  useEffect(() => { if (!embedded) document.getElementById("learning-history-title")?.focus(); }, [embedded]);

  return <section className="dashboard-card learner-history-page" aria-labelledby="learning-history-title">
    {!embedded && <a className="learner-section-link" href={runId ? "#/learning/history" : "#/"}>{runId ? "← Zurück zum Übungsverlauf" : "← Zur Startseite"}</a>}
    {embedded ? <h2 id="learning-history-title" tabIndex={-1}>Übungsverlauf</h2> : <h1 id="learning-history-title" tabIndex={-1}>Übungsverlauf</h1>}
    {error && <div className="alert error" role="alert">{error} <button className="secondary" onClick={() => setRetry((value) => value + 1)}>Erneut laden</button></div>}
    {!runId && loading && <p role="status">Dein Verlauf wird geladen …</p>}
    {runId && <HistoryRunDetail key={runId} runId={runId} />}
    {!runId && runs.length > 0 && <table className="learner-history-list" aria-label="Übungsverlauf">
      <thead><tr><th scope="col">Aufgabe</th><th scope="col">Zeitraum</th><th scope="col">Ergebnis</th><th scope="col">Verlauf</th></tr></thead>
      <tbody>{runs.map((run) => <Fragment key={run.id}>
        <tr className="learner-history-row">
          <th scope="row">{run.title}</th>
          <td><strong>{timestamp(run.startedAt)}</strong><small>{run.durationSeconds === null && run.status !== "STARTED" ? "Dauer nicht gespeichert" : runDurationLabel(run)} · {runStatusLabels[run.status] ?? "Importiert"}</small></td>
          <td><strong><Result run={run} /></strong></td>
          <td><button type="button" className={`analysis-inline-button analysis-icon-button${openRunId === run.id ? " active" : ""}`} aria-expanded={openRunId === run.id} aria-controls={`history-detail-${run.id}`} aria-label={`Aufgabenverlauf ${openRunId === run.id ? "einklappen" : "ausklappen"}: ${run.title}`} title={openRunId === run.id ? "Aufgabenverlauf einklappen" : "Aufgabenverlauf ausklappen"} onClick={() => setOpenRunId(openRunId === run.id ? null : run.id)}>{openRunId === run.id ? "−" : "+"}</button></td>
        </tr>
        <tr hidden={openRunId !== run.id}><td colSpan={4} className="learner-history-expanded"><div id={`history-detail-${run.id}`} className="analysis-inline-detail history">{openRunId === run.id && <HistoryRunDetail runId={run.id} inline />}</div></td></tr>
      </Fragment>)}</tbody>
    </table>}
    {!runId && !loading && !error && runs.length === 0 && <p>Nach deiner ersten Übung erscheint hier dein Verlauf.</p>}
    {!runId && nextCursor && !error && <button className="secondary" disabled={loading} onClick={() => void load(nextCursor)}>Weitere laden</button>}
  </section>;
}
